/**
 * GameSummaryRow — one game as a list "banner", shared by the Games page and the
 * Home page so a game looks and behaves identically in both places.
 *
 * The two team names are rendered separately so a finished game can colour the
 * winner (bold green) and the loser (red); in every other state both names use
 * the neutral link colour. The subtitle carries the Division · time · venue ·
 * court (no league — it is redundant under the league banner), and a finished
 * game shows the per-set score line (e.g. "23-21, 21-19"). The action link is a
 * sibling of the row link (never nested) to keep the markup valid.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { GameStatus } from '@sulis-liiga/common';
import type { DisplayStatus } from '../../utils/gameStatus';
import { STATUS_TONE_CLASS, deriveDisplayStatus } from '../../utils/gameStatus';

export type RowAction =
  /** Approve a submitted result → link to the approve page. */
  | { kind: 'approve'; to: string }
  /** Enter a result for a scheduled game → link to the result page. */
  | { kind: 'enter-result'; to: string }
  /** Propose a still-unclaimed fixture → link to the propose page (autofilled). */
  | { kind: 'propose'; to: string }
  /**
   * Respond to an active proposal. `oneClick` (a single slot on offer) accepts it
   * straight from the banner via the page's `onApproveProposal`; otherwise the
   * button links to the proposal detail page (`to`) to pick a slot.
   */
  | { kind: 'respond-proposal'; to: string; oneClick: boolean; proposalId: string }
  /** Passive "waiting on the other side" text (no button). */
  | { kind: 'awaiting'; text: string };

export interface GameSummaryRowProps {
  /** Link target for the row (game detail, or the proposal detail for a proposal row). */
  to: string;
  /** First team name (order incidental). */
  teamA: string;
  /** Second team name. */
  teamB: string;
  /**
   * When the game is decided, the winning team's name — used to colour the
   * winner green/bold and the loser red. Omitted (or `decided` false) → neutral.
   */
  winnerName?: string;
  /** Apply win/loss colouring to the team names (finished games only). */
  decided?: boolean;
  /** Secondary line: Division · time · venue · court. */
  subtitle?: string;
  /** Derived display status (label + tone). */
  status: DisplayStatus;
  /** Per-set score line for finished games, e.g. "23-21, 21-19". */
  score?: string;
  /** Optional contextual action shown on the right. */
  action?: RowAction;
}

/**
 * Normalized input describing a game, from which {@link toGameRowProps} derives
 * the display status and contextual action. Both the Games page (from a `Game`)
 * and the Home page (from a `MyGameSummary`) build this shape and call the same
 * function, so a game behaves identically in both places.
 */
export interface GameRowInput {
  gameId: string;
  teamA: string;
  teamB: string;
  /** Secondary line: Division · time · venue · court. */
  subtitle?: string;
  /** Override the row link (default `/games/:gameId`); proposal rows point at the proposal. */
  rowHref?: string;
  /** Persisted lifecycle status. */
  status: GameStatus;
  /** A real (scored) result has been entered. */
  hasResult: boolean;
  /** The result has been approved by both teams (game completed). */
  resultVerified: boolean;
  /** The viewer is a member of one of the two teams. */
  isParticipant: boolean;
  /** An unverified result exists and the viewer still needs to approve it. */
  awaitingMyApproval: boolean;
  /**
   * The active proposal on a PENDING fixture, if any. Drives the "Pending
   * Proposal" label and the responder's Approve action. Absent/null when the
   * fixture has not been proposed ("Pending to Propose").
   */
  activeProposal?: {
    proposalId: string;
    /** One of the viewer's teams is the team that must respond next. */
    respondingByMe: boolean;
    /** Live slots on offer — a single slot enables one-click approve. */
    slotCount: number;
  } | null;
  /** Per-set score line for finished games, e.g. "23-21, 21-19". */
  score?: string;
  /** Winner team name (for win/loss colouring on completed games). */
  winnerName?: string;
  /** When CANCELLED: 'abandoned' renders as amber "Abandoned" badge instead of grey "Cancelled". */
  cancellationReason?: 'cancelled' | 'abandoned';
}

/**
 * The single source of truth for how a game maps to a banner: which status label
 * to show (Scheduled / Played / Completed / …) and which action the viewer gets
 * (Approve / Awaiting approval / Enter result / none).
 */
export function toGameRowProps(input: GameRowInput): GameSummaryRowProps {
  const to = input.rowHref ?? `/games/${input.gameId}`;

  // Atomic fixture states, handled before the scheduled/played/completed
  // derivation below. PENDING = "Pending to Propose" (nobody has claimed it);
  // PROPOSED = "Pending Proposal" (an open proposal is under negotiation). The two
  // are disjoint, so a claimed fixture never appears on the "to propose" list.
  if (input.status === GameStatus.PROPOSED) {
    let action: RowAction | undefined;
    if (input.activeProposal?.respondingByMe) {
      // It's the viewer's turn to respond — Approve (one-click when a single slot
      // is on offer, otherwise open the proposal to pick one).
      action = {
        kind: 'respond-proposal',
        to: `/games/proposals/${input.activeProposal.proposalId}`,
        oneClick: input.activeProposal.slotCount === 1,
        proposalId: input.activeProposal.proposalId,
      };
    } else if (input.isParticipant) {
      // The viewer made the latest offer — waiting on the opponent.
      action = { kind: 'awaiting', text: 'Awaiting response' };
    }
    return {
      to,
      teamA: input.teamA,
      teamB: input.teamB,
      subtitle: input.subtitle,
      status: { label: 'Pending Proposal', tone: 'pending' },
      action,
    };
  }
  if (input.status === GameStatus.PENDING) {
    // No proposal yet — a participant can propose it (fixture pre-filled).
    const action: RowAction | undefined = input.isParticipant
      ? { kind: 'propose', to: `/games/proposals/create?gameId=${input.gameId}` }
      : undefined;
    return {
      to,
      teamA: input.teamA,
      teamB: input.teamB,
      subtitle: input.subtitle,
      status: { label: 'Pending to Propose', tone: 'pending' },
      action,
    };
  }

  const hasUnverifiedResult = input.hasResult && !input.resultVerified;
  const status = deriveDisplayStatus(input.status, hasUnverifiedResult, input.cancellationReason);
  let action: RowAction | undefined;
  if (input.awaitingMyApproval) {
    // A result was entered by the other side and awaits this viewer's approval.
    action = { kind: 'approve', to: `/games/${input.gameId}/approve` };
  } else if (input.isParticipant && hasUnverifiedResult) {
    // The viewer's side entered the result; waiting on the opponent to approve.
    action = { kind: 'awaiting', text: 'Awaiting approval' };
  } else if (input.isParticipant && !input.hasResult) {
    // Scheduled, no result yet — the viewer can enter it.
    action = { kind: 'enter-result', to: `/games/${input.gameId}/result` };
  }
  return {
    to,
    teamA: input.teamA,
    teamB: input.teamB,
    winnerName: input.winnerName,
    decided: status.tone === 'completed',
    subtitle: input.subtitle,
    status,
    score: input.score,
    action,
  };
}

const GameSummaryRow: React.FC<
  GameSummaryRowProps & {
    /**
     * One-click proposal approval. When provided and the action is a single-slot
     * `respond-proposal`, the Approve button calls this (accepting the only slot)
     * instead of navigating to the proposal detail page.
     */
    onApproveProposal?: (proposalId: string) => void;
  }
> = ({
  to,
  teamA,
  teamB,
  winnerName,
  decided,
  subtitle,
  status,
  score,
  action,
  onApproveProposal,
}) => {
  const nameClass = (name: string): string => {
    if (decided && winnerName) {
      return name === winnerName ? 'font-bold text-success-700' : 'text-danger-600';
    }
    return 'text-primary-600 font-medium';
  };

  return (
    <div className="px-4 py-4 flex items-center justify-between gap-3 hover:bg-gray-50">
      <Link to={to} className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm truncate">
            <span className={nameClass(teamA)}>{teamA}</span>
            <span className="text-gray-400 font-normal"> vs </span>
            <span className={nameClass(teamB)}>{teamB}</span>
          </span>
          <span
            className={`shrink-0 text-xs px-2 py-0.5 rounded-full ${
              STATUS_TONE_CLASS[status.tone]
            }`}
          >
            {status.label}
          </span>
        </div>
        {(subtitle || score) && (
          <div className="mt-1 text-sm text-gray-500">
            {subtitle}
            {score && (
              <span
                className={
                  subtitle ? 'ml-2 font-semibold text-gray-800' : 'font-semibold text-gray-800'
                }
              >
                {subtitle ? '· ' : ''}
                {score}
              </span>
            )}
          </div>
        )}
      </Link>

      {action?.kind === 'approve' && (
        <Link
          to={action.to}
          className="shrink-0 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-success-600 hover:bg-success-800"
        >
          Approve
        </Link>
      )}
      {action?.kind === 'enter-result' && (
        <Link
          to={action.to}
          className="shrink-0 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          Enter result
        </Link>
      )}
      {action?.kind === 'propose' && (
        <Link
          to={action.to}
          className="shrink-0 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          Propose
        </Link>
      )}
      {action?.kind === 'respond-proposal' &&
        (action.oneClick && onApproveProposal ? (
          <button
            type="button"
            onClick={() => onApproveProposal(action.proposalId)}
            className="shrink-0 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-success-600 hover:bg-success-800"
          >
            Approve
          </button>
        ) : (
          <Link
            to={action.to}
            className="shrink-0 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-success-600 hover:bg-success-800"
          >
            Approve
          </Link>
        ))}
      {action?.kind === 'awaiting' && (
        <span className="shrink-0 text-xs font-medium text-amber-700">{action.text}</span>
      )}
    </div>
  );
};

export default GameSummaryRow;
